/**
 * Fading ribbon trails for thrown trash. A small pool of line strips; each
 * follows one piece's mesh until it slows down, then fades. Trails reset on
 * teleport (a >5m frame jump) instead of streaking across the arena.
 */
import * as THREE from "three";
import type { TrashPiece } from "../gameplay/Trash.js";

const POOL = 8;
const POINTS = 18;

interface Trail {
  piece: TrashPiece | null;
  line: THREE.Line;
  positions: Float32Array;
  colors: Float32Array;
  head: THREE.Vector3;
  filled: number;
  fade: number; // 1 → 0 once released
}

export class Trails {
  readonly group = new THREE.Group();
  private readonly trails: Trail[] = [];

  constructor() {
    for (let i = 0; i < POOL; i++) {
      const positions = new Float32Array(POINTS * 3);
      const colors = new Float32Array(POINTS * 3);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
      const line = new THREE.Line(
        geo,
        new THREE.LineBasicMaterial({
          vertexColors: true,
          transparent: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      );
      line.visible = false;
      line.frustumCulled = false;
      this.group.add(line);
      this.trails.push({
        piece: null,
        line,
        positions,
        colors,
        head: new THREE.Vector3(),
        filled: 0,
        fade: 1,
      });
    }
  }

  /** Attach a trail to a just-thrown piece (steals the oldest if the pool is dry). */
  follow(piece: TrashPiece, color: THREE.Color | number): void {
    const t = this.trails.find((t) => t.piece === null && t.fade <= 0.01) ?? this.trails[0]!;
    t.piece = piece;
    t.fade = 1;
    t.filled = 0;
    t.head.copy(piece.mesh.position);
    const c = color instanceof THREE.Color ? color : new THREE.Color(color);
    for (let i = 0; i < POINTS; i++) {
      t.positions[i * 3] = piece.mesh.position.x;
      t.positions[i * 3 + 1] = piece.mesh.position.y;
      t.positions[i * 3 + 2] = piece.mesh.position.z;
      const w = 1 - i / (POINTS - 1);
      t.colors[i * 3] = c.r * w;
      t.colors[i * 3 + 1] = c.g * w;
      t.colors[i * 3 + 2] = c.b * w;
    }
    t.line.visible = true;
  }

  update(): void {
    for (const t of this.trails) {
      if (!t.line.visible) continue;
      if (t.piece) {
        const m = t.piece.mesh.position;
        // teleport → restart the ribbon at the exit, don't streak
        if (t.head.distanceToSquared(m) > 25) {
          for (let i = 0; i < POINTS; i++) {
            t.positions[i * 3] = m.x;
            t.positions[i * 3 + 1] = m.y;
            t.positions[i * 3 + 2] = m.z;
          }
        } else {
          // shift history back, write new head
          for (let i = POINTS - 1; i > 0; i--) {
            t.positions[i * 3] = t.positions[(i - 1) * 3];
            t.positions[i * 3 + 1] = t.positions[(i - 1) * 3 + 1];
            t.positions[i * 3 + 2] = t.positions[(i - 1) * 3 + 2];
          }
          t.positions[0] = m.x;
          t.positions[1] = m.y;
          t.positions[2] = m.z;
        }
        t.head.copy(m);

        // release once the piece settles or dies
        const lv = t.piece.live ? t.piece.body.linvel() : null;
        if (!t.piece.live || !lv || Math.hypot(lv.x, lv.y, lv.z) < 3.5 || t.piece.held) {
          t.piece = null;
        }
      } else {
        t.fade -= 0.08;
        if (t.fade <= 0) {
          t.line.visible = false;
          continue;
        }
      }
      (t.line.material as THREE.LineBasicMaterial).opacity = t.fade;
      (t.line.geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
    }
  }
}
